import React, { useEffect, useRef, useState } from "react";
import { chat } from "../data/data";
import "./Chat.css";

// Funkcja do generowania unikalnego ID sesji
function generateSessionId() {
  return Date.now().toString(36) + Math.random().toString(36).substr(2, 9);
}

const Chat = () => {
  const [open, setOpen] = useState(false);
  const [showBubble, setShowBubble] = useState(false);
  const [step, setStep] = useState(0); // 0: intro, 1: options, 2: form, 3: chat
  const [selectedOption, setSelectedOption] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [emailError, setEmailError] = useState("");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [thankYou, setThankYou] = useState(false);
  const [sessionId, setSessionId] = useState("");
  const chatRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => setShowBubble(true), chat.autoOpenDelay);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (chatRef.current) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }
  }, [messages]);

  // Generuj sessionId przy pierwszym otwarciu chatu
  useEffect(() => {
    if (open && !sessionId) {
      setSessionId(generateSessionId());
    }
  }, [open, sessionId]);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleOptionSelect = (option) => {
    setSelectedOption(option);
    setStep(2); // Przejdź do formularza
  };

  const handleStart = (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      setEmailError(chat.nameRequired);
      return;
    }
    if (!form.email.trim() || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) {
      setEmailError(chat.emailRequired);
      return;
    }
    setEmailError("");
    setStep(3);
    // Dodaj odpowiednią wiadomość w zależności od wybranej opcji
    let welcomeMessage =
      "Świetnie! Teraz możesz napisać wiadomość do Mateusza.";
    if (selectedOption === "career") {
      welcomeMessage =
        chat.responses.career +
        "\n\nTeraz możesz zadać dodatkowe pytania o karierę.";
    } else if (selectedOption === "skills") {
      welcomeMessage =
        chat.responses.skills +
        "\n\nTeraz możesz zadać dodatkowe pytania o umiejętności.";
    } else if (selectedOption === "contact") {
      welcomeMessage =
        chat.responses.contact +
        "\n\nTeraz możesz napisać wiadomość do Mateusza.";
    }
    setMessages([{ from: "bot", text: welcomeMessage }]);
  };

  // Timer na automatyczną odpowiedź po 3 minutach
  const autoReplyTimeout = useRef(null);

  const clearAutoReplyTimeout = () => {
    if (autoReplyTimeout.current) {
      clearTimeout(autoReplyTimeout.current);
      autoReplyTimeout.current = null;
    }
  };

  // Funkcja obsługująca wysyłanie wiadomości do n8n (webhook)
  // oraz oczekiwanie na odpowiedź z n8n
  const handleSend = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    setSending(true);
    const now = new Date().toISOString();
    const newMsg = { from: "user", text: input, date: now };
    setMessages((prev) => [...prev, newMsg]);
    setInput("");

    // Mapowanie opcji na tematy
    const topicMap = {
      career: "kariera",
      skills: "umiejętności",
      contact: "kontakt"
    };

    // Anuluj poprzedni timeout jeśli był ustawiony
    clearAutoReplyTimeout();

    // Ustaw timeout na automatyczną odpowiedź po 3 minutach (180000 ms)
    autoReplyTimeout.current = setTimeout(() => {
      setMessages((prev) => [
        ...prev,
        { from: "bot", text: chat.thankYou, date: new Date().toISOString() }
      ]);
      autoReplyTimeout.current = null;
    }, 180000);

    try {
      // Wysyłka wiadomości do n8n (webhook)
      const response = await fetch(chat.webhook, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          sessionId,
          name: form.name,
          email: form.email,
          phone: form.phone,
          message: input,
          option: selectedOption,
          temat: topicMap[selectedOption] || "nieznany",
          date: now
        })
      });
      let botResponse = null;
      // Oczekiwanie na odpowiedź z n8n (jeśli webhook zwróci response)
      if (response.ok) {
        const data = await response.json().catch(() => null);
        if (data && (data.response || data.message)) {
          botResponse = data.response || data.message;
        }
      }
      if (botResponse) {
        // Jeśli przyszła odpowiedź, anuluj timeout i pokaż odpowiedź
        clearAutoReplyTimeout();
        setMessages((prev) => [
          ...prev,
          { from: "bot", text: botResponse, date: new Date().toISOString() }
        ]);
      }
    } catch {
      // Błąd wysyłki do n8n
      clearAutoReplyTimeout();
      setMessages((prev) => [
        ...prev,
        {
          from: "bot",
          text: "Błąd wysyłki. Spróbuj ponownie.",
          date: new Date().toISOString()
        }
      ]);
    } finally {
      setSending(false);
    }
  };

  const resetChat = () => {
    setStep(0);
    setSelectedOption(null);
    setForm({ name: "", email: "", phone: "" });
    setMessages([]);
    setInput("");
    setEmailError("");
    setSessionId("");
  };

  return (
    <div className={`chat-widget${open ? " open" : ""}`}>
      <button
        className="chat-toggle"
        onClick={() => {
          setOpen((v) => !v);
          setShowBubble(false);
          if (!open) {
            resetChat();
          }
        }}
        aria-label="Otwórz/zamknij czat">
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="12" fill="#ffb400" />
          <path
            d="M7 10h10M7 14h6"
            stroke="#23233a"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      </button>
      {showBubble && !open && (
        <div
          className="chat-bubble"
          onClick={() => {
            setOpen(true);
            setShowBubble(false);
            resetChat();
          }}>
          {chat.welcome}
        </div>
      )}
      {open && (
        <div className="chat-window">
          {step === 0 && (
            <div className="chat-options">
              <div className="chat-options-header">
                <h3>Wybierz opcję:</h3>
              </div>
              <div className="chat-options-list">
                <button
                  className="chat-option"
                  onClick={() => handleOptionSelect("career")}>
                  {chat.options.career}
                </button>
                <button
                  className="chat-option"
                  onClick={() => handleOptionSelect("skills")}>
                  {chat.options.skills}
                </button>
                <button
                  className="chat-option"
                  onClick={() => handleOptionSelect("contact")}>
                  {chat.options.contact}
                </button>
              </div>
            </div>
          )}
          {step === 2 && selectedOption !== "contact" && (
            <form className="chat-form" onSubmit={handleStart}>
              <div className="chat-form-header">
                <h3>Formularz kontaktowy</h3>
                <p>Wypełnij dane, aby kontynuować</p>
              </div>
              <label>
                {chat.askName}
                <input
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={handleFormChange}
                  required
                />
              </label>
              <label>
                {chat.askEmail}
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleFormChange}
                  required
                />
              </label>
              <label>
                {chat.askPhone}
                <input
                  type="tel"
                  name="phone"
                  value={form.phone}
                  onChange={handleFormChange}
                />
              </label>
              {emailError && <div className="chat-error">{emailError}</div>}
              <button type="submit">{chat.startChat}</button>
            </form>
          )}
          {step === 2 && selectedOption === "contact" && !thankYou && (
            <form
              className="chat-form"
              onSubmit={async (e) => {
                e.preventDefault();
                if (!form.name.trim()) {
                  setEmailError(chat.nameRequired);
                  return;
                }
                if (
                  !form.email.trim() ||
                  !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)
                ) {
                  setEmailError(chat.emailRequired);
                  return;
                }
                setEmailError("");
                setSending(true);
                try {
                  await fetch(chat.webhook, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                      sessionId,
                      name: form.name,
                      email: form.email,
                      phone: form.phone,
                      option: "contact",
                      temat: "kontakt",
                      date: new Date().toISOString(),
                      message: "(formularz kontaktowy czatu)"
                    })
                  });
                  setThankYou(true);
                } catch {
                  setEmailError("Błąd wysyłki. Spróbuj ponownie.");
                } finally {
                  setSending(false);
                }
              }}>
              <div className="chat-form-header">
                <h3>Formularz kontaktowy</h3>
                <p>Wypełnij dane, aby kontynuować</p>
              </div>
              <label>
                {chat.askName}
                <input
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={handleFormChange}
                  required
                />
              </label>
              <label>
                {chat.askEmail}
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleFormChange}
                  required
                />
              </label>
              <label>
                {chat.askPhone}
                <input
                  type="tel"
                  name="phone"
                  value={form.phone}
                  onChange={handleFormChange}
                />
              </label>
              {emailError && <div className="chat-error">{emailError}</div>}
              <button type="submit" disabled={sending}>
                {sending ? "Wysyłanie..." : "Dalej"}
              </button>
            </form>
          )}
          {step === 2 && selectedOption === "contact" && thankYou && (
            <div className="chat-contact-buttons">
              <div className="chat-form-header">
                <h3>Wybierz sposób kontaktu</h3>
                <p>
                  Możesz skontaktować się bezpośrednio przez wybraną platformę:
                </p>
              </div>
              <a
                href="https://discord.com/users/mathy_mts"
                target="_blank"
                rel="noopener noreferrer"
                className="chat-option"
                style={{ textAlign: "center" }}>
                Discord
              </a>
              <a
                href="https://wa.me/48793208601"
                target="_blank"
                rel="noopener noreferrer"
                className="chat-option"
                style={{ textAlign: "center" }}>
                WhatsApp
              </a>
              <a
                href="mailto:mathy1987s@gmail.com"
                className="chat-option"
                style={{ textAlign: "center" }}>
                E-mail
              </a>
            </div>
          )}
          {step === 3 && (
            <div className="chat-body">
              <div className="chat-messages" ref={chatRef}>
                {messages.map((msg, i) => (
                  <div key={i} className={`chat-msg chat-msg-${msg.from}`}>
                    {msg.text}
                    {msg.date && (
                      <div
                        style={{
                          fontSize: "0.75rem",
                          color: "#888",
                          marginTop: 2
                        }}>
                        {new Date(msg.date).toLocaleString("pl-PL", {
                          hour: "2-digit",
                          minute: "2-digit",
                          second: "2-digit"
                        })}
                      </div>
                    )}
                  </div>
                ))}
                {sending && (
                  <div
                    className="chat-msg chat-msg-bot"
                    style={{ opacity: 0.7 }}>
                    Oczekiwanie na odpowiedź...
                  </div>
                )}
              </div>
              {!thankYou && (
                <form className="chat-send" onSubmit={handleSend}>
                  <input
                    type="text"
                    placeholder={chat.placeholder}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    disabled={false}
                  />
                  <button type="submit" disabled={!input.trim()}>
                    {chat.sendLabel}
                  </button>
                </form>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Chat;
